import type { EventBus } from '@/core/EventBus';
import type { GameController } from '@/game/GameController';
import { GameStateEvents, GamePhase } from '@/game/GameStateManager';
import { PlacementEvents } from '@/placement/PlacementSystem';
import { ShopEvents } from '@/shop/ShopSystem';
import { RoundEvents } from '@/game/RoundManager';
import { EconomyEvents } from '@/economy/CurrencyManager';
import { NetworkEvents } from '@/network/NetworkManager';
import { PlayerService, PlayerEvents } from '@/player/PlayerService';
import { ShopPanel } from './ShopPanel';
import { BottomNavBar, NavTab } from './BottomNavBar';
import { CharactersPanel } from './CharactersPanel';
import { BattleLobbyPanel } from './BattleLobbyPanel';
import { PlayerNameModal } from './PlayerNameModal';
import { BattleIntroOverlay } from './BattleIntroOverlay';

/**
 * UI Manager - Creates and updates DOM-based UI overlay
 */
export class UIManager {
  private container: HTMLElement;
  private eventBus: EventBus;
  private gameController: GameController;

  // UI Elements
  private hudElement: HTMLElement | null = null;
  private phaseIndicator: HTMLElement | null = null;
  private roundIndicator: HTMLElement | null = null;
  private timerIndicator: HTMLElement | null = null;
  private goldIndicator: HTMLElement | null = null;
  private unitCountIndicator: HTMLElement | null = null;
  private traitPanel: HTMLElement | null = null;
  private shopPanel: ShopPanel | null = null;
  private roundResultOverlay: HTMLElement | null = null;
  private bottomNavBar: BottomNavBar | null = null;
  private charactersPanel: CharactersPanel | null = null;
  private battleLobbyPanel: BattleLobbyPanel | null = null;
  private playerService: PlayerService;
  private playerNameModal: PlayerNameModal | null = null;
  private goldLabel: HTMLElement | null = null;
  private playerNamesDisplay: HTMLElement | null = null;
  private battleIntroOverlay: BattleIntroOverlay | null = null;
  private toastContainer: HTMLElement | null = null;

  constructor(
    container: HTMLElement,
    eventBus: EventBus,
    gameController: GameController
  ) {
    this.container = container;
    this.eventBus = eventBus;
    this.gameController = gameController;

    // Create player service first
    this.playerService = new PlayerService(eventBus);

    this.createUI();
    this.setupEventListeners();
    this.update();

    // Create shop panel
    this.shopPanel = new ShopPanel(container, eventBus, gameController, this.playerService);

    // Create characters panel
    this.charactersPanel = new CharactersPanel(container);

    // Create player name modal
    this.playerNameModal = new PlayerNameModal(container, this.playerService);

    // Create battle intro overlay
    this.battleIntroOverlay = new BattleIntroOverlay(container, () => {
      this.onBattleIntroComplete();
    });

    // Create battle lobby panel
    this.battleLobbyPanel = new BattleLobbyPanel(container, {
      onEnterBattle: () => this.enterBattleView()
    });
    this.battleLobbyPanel.setGameContext(eventBus, gameController, this.playerService, this.playerNameModal);

    // Create bottom navigation bar
    this.bottomNavBar = new BottomNavBar(container, {
      onTabChange: (tab) => this.switchView(tab)
    });

    // Initially show battle lobby (hide game elements)
    this.showBattleLobby();

    // Show player name modal if no name is set
    if (!this.playerService.hasName()) {
      this.playerNameModal.show(false);
    }
  }

  /**
   * Show the battle lobby (with the big Battle button)
   */
  private showBattleLobby(): void {
    const gameCanvas = document.getElementById('game-canvas');
    if (gameCanvas) gameCanvas.style.display = 'none';
    if (this.hudElement) this.hudElement.style.display = 'none';
    this.shopPanel?.hide();
    this.battleLobbyPanel?.show();
  }

  /**
   * Enter the actual battle view (hide bottom nav, show game)
   */
  private enterBattleView(): void {
    const gameCanvas = document.getElementById('game-canvas');
    if (gameCanvas) gameCanvas.style.display = 'block';
    if (this.hudElement) this.hudElement.style.display = 'block';
    this.shopPanel?.show();
    this.battleLobbyPanel?.hide();
    this.bottomNavBar?.hide();

    // Start the game if not already in multiplayer mode
    if (!this.gameController.isMultiplayerMode()) {
      this.gameController.startGame();
      this.gameController.skipRulerSelection();
    } else {
      console.log('Multiplayer game starting...');
    }
  }

  /**
   * Switch between battle and characters views
   */
  private switchView(tab: NavTab): void {
    if (tab === 'battle') {
      // Show battle lobby (not the game itself)
      this.charactersPanel?.hide();
      this.showBattleLobby();
    } else {
      // Show characters view
      const gameCanvas = document.getElementById('game-canvas');
      if (gameCanvas) gameCanvas.style.display = 'none';
      if (this.hudElement) this.hudElement.style.display = 'none';
      this.shopPanel?.hide();
      this.battleLobbyPanel?.hide();
      this.charactersPanel?.show();
    }
  }

  /**
   * Create the UI elements
   */
  private createUI(): void {
    // Create HUD container
    this.hudElement = document.createElement('div');
    this.hudElement.id = 'game-hud';
    this.hudElement.innerHTML = `
      <style>
        #game-hud {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          pointer-events: none;
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
          color: white;
          text-shadow: 1px 1px 2px rgba(0,0,0,0.8);
        }
        #game-hud > * {
          pointer-events: auto;
        }
        .hud-top {
          display: flex;
          justify-content: space-between;
          padding: 10px 20px;
          background: linear-gradient(to bottom, rgba(0,0,0,0.6), transparent);
          position: relative;
        }
        .hud-section {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        .hud-label {
          font-size: 11px;
          text-transform: uppercase;
          opacity: 0.7;
        }
        .hud-value {
          font-size: 20px;
          font-weight: bold;
        }
        .hud-center {
          text-align: center;
        }
        .phase-indicator {
          font-size: 18px;
          font-weight: bold;
          color: #ffd700;
        }
        .timer {
          font-size: 22px;
          font-family: monospace;
        }
        .trait-panel {
          position: absolute;
          left: 10px;
          top: 110px;
          background: rgba(0,0,0,0.7);
          padding: 10px;
          border-radius: 8px;
          min-width: 150px;
        }
        .trait-title {
          font-size: 14px;
          font-weight: bold;
          margin-bottom: 8px;
          border-bottom: 1px solid rgba(255,255,255,0.3);
          padding-bottom: 4px;
        }
        .trait-item {
          display: flex;
          justify-content: space-between;
          font-size: 12px;
          padding: 2px 0;
        }
        .trait-active {
          color: #4CAF50;
        }
        .trait-inactive {
          color: #888;
        }
        .gold-indicator {
          color: #ffd700;
        }
        .round-result-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.8);
          display: none;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          z-index: 500;
        }
        .round-result-overlay.visible {
          display: flex;
        }
        .round-result-title {
          font-size: 64px;
          font-weight: bold;
          margin-bottom: 20px;
          text-transform: uppercase;
          letter-spacing: 4px;
        }
        .round-result-title.victory {
          color: #4ade80;
          text-shadow: 0 0 20px rgba(74, 222, 128, 0.5);
        }
        .round-result-title.defeat {
          color: #f87171;
          text-shadow: 0 0 20px rgba(248, 113, 113, 0.5);
        }
        .round-result-title.draw {
          color: #fbbf24;
          text-shadow: 0 0 20px rgba(251, 191, 36, 0.5);
        }
        .round-result-score {
          font-size: 48px;
          font-weight: bold;
          margin-bottom: 10px;
        }
        .round-result-score .p1 {
          color: #3b82f6;
        }
        .round-result-score .p2 {
          color: #ef4444;
        }
        .round-result-label {
          font-size: 18px;
          color: #9ca3af;
          margin-bottom: 30px;
        }
        .round-result-countdown {
          font-size: 16px;
          color: #6b7280;
        }
        .round-result-btn {
          margin-top: 20px;
          padding: 12px 32px;
          font-size: 18px;
          font-weight: bold;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s;
          background: #4CAF50;
          color: white;
          display: none;
        }
        .round-result-btn:hover {
          transform: scale(1.05);
          background: #45a049;
        }
        .round-result-btn.visible {
          display: block;
        }
        .phase-display-center {
          position: absolute;
          top: 90px;
          left: 50%;
          transform: translateX(-50%);
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 4px;
          padding: 8px 20px;
          background: rgba(0, 0, 0, 0.7);
          border-radius: 8px;
          z-index: 100;
        }
        .toast-container {
          position: fixed;
          top: 120px;
          left: 50%;
          transform: translateX(-50%);
          z-index: 1000;
          display: flex;
          flex-direction: column;
          gap: 8px;
          pointer-events: none;
        }
        .toast {
          padding: 12px 24px;
          border-radius: 8px;
          font-size: 16px;
          font-weight: bold;
          animation: toastSlideIn 0.3s ease-out, toastFadeOut 0.3s ease-in 1.7s forwards;
          text-align: center;
        }
        .toast.warning {
          background: rgba(251, 191, 36, 0.95);
          color: #1f2937;
          border: 2px solid #f59e0b;
        }
        .toast.error {
          background: rgba(239, 68, 68, 0.95);
          color: white;
          border: 2px solid #dc2626;
        }
        @keyframes toastSlideIn {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes toastFadeOut {
          from {
            opacity: 1;
          }
          to {
            opacity: 0;
          }
        }
      </style>

      <div class="hud-top">
        <div class="hud-section hud-center">
          <span class="hud-label">Round</span>
          <span class="hud-value" id="round-display">1</span>
        </div>

        <div class="hud-section" style="text-align: right;">
          <span class="hud-label">Units</span>
          <span class="hud-value" id="units-display">0 vs 0</span>
        </div>
      </div>

      <div class="phase-display-center" id="phase-display-center">
        <span class="phase-indicator" id="phase-display">Preparation</span>
        <span class="timer" id="timer-display">0:20</span>
      </div>

      `;

    this.container.appendChild(this.hudElement);

    // Create toast container
    this.toastContainer = document.createElement('div');
    this.toastContainer.className = 'toast-container';
    this.container.appendChild(this.toastContainer);

    // Create round result overlay
    this.roundResultOverlay = document.createElement('div');
    this.roundResultOverlay.className = 'round-result-overlay';
    this.roundResultOverlay.innerHTML = `
      <div class="round-result-title" id="round-result-title">VICTORY</div>
      <div class="round-result-score">
        <span class="p1" id="result-score-p1">0</span>
        <span> - </span>
        <span class="p2" id="result-score-p2">0</span>
      </div>
      <div class="round-result-label">Total Score</div>
      <div class="round-result-countdown" id="round-result-countdown">Next round in 3...</div>
      <button class="round-result-btn" id="btn-back-to-menu">Back to Menu</button>
    `;
    this.container.appendChild(this.roundResultOverlay);

    // Cache element references
    this.phaseIndicator = document.getElementById('phase-display');
    this.roundIndicator = document.getElementById('round-display');
    this.timerIndicator = document.getElementById('timer-display');
    this.goldIndicator = document.getElementById('gold-display');
    this.unitCountIndicator = document.getElementById('units-display');
    this.traitPanel = document.getElementById('trait-list');
    this.goldLabel = document.getElementById('gold-label');
    this.playerNamesDisplay = document.getElementById('player-names');

    // Setup button handlers
    this.setupButtonHandlers();

    // Set initial gold label with player name
    this.updateGoldLabel();
  }

  /**
   * Setup button click handlers
   */
  private setupButtonHandlers(): void {
    const backToMenuBtn = document.getElementById('btn-back-to-menu');

    backToMenuBtn?.addEventListener('click', () => {
      this.hideRoundResult();
      this.gameController.returnToMainMenu();
      // Show the battle lobby and bottom nav again
      this.showBattleLobby();
      this.bottomNavBar?.show();
    });

    // Space key to start battle during preparation phase (single player only)
    window.addEventListener('keydown', this.onKeyDown);
  }

  /**
   * Handle keyboard input
   */
  private onKeyDown = (event: KeyboardEvent): void => {
    // Space key starts battle during preparation phase (single player only)
    if (event.code === 'Space') {
      const info = this.gameController.getGameInfo();
      if (info.phase === GamePhase.Preparation && !this.gameController.isMultiplayerMode()) {
        event.preventDefault();
        this.gameController.gameState.startBattle();
      }
    }
  };

  /**
   * Setup event listeners
   */
  private setupEventListeners(): void {
    // Update on phase change
    this.eventBus.on<{ previousPhase: GamePhase; newPhase: GamePhase }>(
      GameStateEvents.PHASE_CHANGED,
      (data) => {
        this.update();
        // Hide round result overlay when entering preparation phase
        if (data.newPhase === GamePhase.Preparation) {
          this.hideRoundResult();
        }
        // Show player names during preparation and battle phases
        this.showPlayerNames(data.newPhase === GamePhase.Preparation || data.newPhase === GamePhase.Battle);
      }
    );

    // Update on timer tick
    this.eventBus.on(GameStateEvents.TIMER_TICK, () => {
      this.updateTimer();
    });

    // Update on gold change
    this.eventBus.on(EconomyEvents.GOLD_CHANGED, () => {
      this.updateGold();
    });

    // Update on round end (single player) - get winner from game state
    this.eventBus.on<{ winnerId: number }>(GameStateEvents.ROUND_ENDED, (data) => {
      this.update();
      // Only show in single player mode (multiplayer uses ROUND_RESULT)
      if (!this.gameController.isMultiplayerMode()) {
        this.showRoundResult(data.winnerId as 0 | 1 | 2);
      }
    });

    // Update on round result (multiplayer)
    this.eventBus.on<{ winnerId: 0 | 1 | 2; scores: { playerId: number; score: number }[] }>(
      NetworkEvents.ROUND_RESULT,
      (data) => {
        // Pass scores directly from server to ensure accuracy
        const p1Score = data.scores.find(s => s.playerId === 1)?.score ?? 0;
        const p2Score = data.scores.find(s => s.playerId === 2)?.score ?? 0;
        this.showRoundResult(data.winnerId, p1Score, p2Score);
      }
    );

    // Update traits when units change
    this.eventBus.on('traits:updated', () => {
      this.updateTraits();
    });

    // Update unit count when units are placed, swapped, or sold
    this.eventBus.on(PlacementEvents.UNIT_PLACED, () => {
      this.updateUnitCount();
    });

    this.eventBus.on(PlacementEvents.UNIT_SWAPPED, () => {
      this.updateUnitCount();
    });

    this.eventBus.on(ShopEvents.UNIT_SOLD, () => {
      this.updateUnitCount();
    });

    // Show feedback when placement fails
    this.eventBus.on<{ reason: string; coord: { q: number; r: number } }>(
      PlacementEvents.INVALID_PLACEMENT,
      (data) => {
        if (data.reason === 'Max team size reached') {
          const maxUnits = this.gameController.getMaxUnitsForCurrentRound();
          this.showToast(`Team full! Max ${maxUnits} units this round`, 'warning');
        }
      }
    );

    // Handle match end (single player)
    this.eventBus.on<{ winnerId: number }>(GameStateEvents.MATCH_ENDED, (data) => {
      this.showRoundResult(data.winnerId as 0 | 1 | 2, undefined, undefined, true);
    });

    // Handle match end (multiplayer)
    this.eventBus.on<{ winnerId: 1 | 2; finalScores: { playerId: number; score: number }[] }>(
      NetworkEvents.MATCH_RESULT,
      (data) => {
        const p1Score = data.finalScores.find(s => s.playerId === 1)?.score ?? 0;
        const p2Score = data.finalScores.find(s => s.playerId === 2)?.score ?? 0;
        this.showRoundResult(data.winnerId, p1Score, p2Score, true);
      }
    );

    // Update gold label when player name changes
    this.eventBus.on(PlayerEvents.NAME_CHANGED, () => {
      this.updateGoldLabel();
      this.updatePlayerNames();
    });

    // Update opponent name when match is found
    this.eventBus.on(NetworkEvents.MATCH_FOUND, () => {
      this.updatePlayerNames();
    });

    // Show match intro when round 1 preparation starts
    this.eventBus.on(RoundEvents.MATCH_INTRO, () => {
      console.log('[UIManager] MATCH_INTRO received');
      this.showBattleIntro();
    });
  }

  /**
   * Update all UI elements
   */
  update(): void {
    this.updatePhase();
    this.updateRound();
    this.updateTimer();
    this.updateGold();
    this.updateUnitCount();
    this.updateTraits();
  }

  /**
   * Update phase display
   */
  private updatePhase(): void {
    if (!this.phaseIndicator) return;
    const info = this.gameController.getGameInfo();

    const phaseNames: Record<GamePhase, string> = {
      [GamePhase.MainMenu]: 'Main Menu',
      [GamePhase.RulerSelect]: 'Select Ruler',
      [GamePhase.Preparation]: 'Preparation',
      [GamePhase.Battle]: 'BATTLE!',
      [GamePhase.RoundEnd]: 'Round Over',
      [GamePhase.MatchEnd]: 'Match Over',
    };

    this.phaseIndicator.textContent = phaseNames[info.phase] || info.phase;
  }

  /**
   * Update round display
   */
  private updateRound(): void {
    if (!this.roundIndicator) return;
    const info = this.gameController.getGameInfo();
    this.roundIndicator.textContent = String(info.round || 1);
  }

  /**
   * Update timer display
   */
  private updateTimer(): void {
    if (!this.timerIndicator) return;
    const info = this.gameController.getGameInfo();
    const seconds = Math.ceil(info.timeRemaining);
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    this.timerIndicator.textContent = `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  /**
   * Update gold display
   */
  private updateGold(): void {
    if (!this.goldIndicator) return;
    const info = this.gameController.getGameInfo();
    this.goldIndicator.textContent = String(info.player1Gold);
  }

  /**
   * Update gold label with player name
   */
  private updateGoldLabel(): void {
    if (!this.goldLabel) return;
    const name = this.playerService.getName();
    this.goldLabel.textContent = `Gold (${name})`;
  }

  /**
   * Update player names display
   */
  private updatePlayerNames(): void {
    const p1NameEl = document.getElementById('p1-name');
    const p2NameEl = document.getElementById('p2-name');
    if (!p1NameEl || !p2NameEl) return;

    const localPlayerName = this.playerService.getName();
    const opponentName = this.gameController.getOpponentName();
    const localPlayerId = this.gameController.getLocalPlayerId();

    // Color based on actual player ID: Player 1 = blue, Player 2 = red
    // Local player is always shown first
    p1NameEl.textContent = localPlayerName;
    p2NameEl.textContent = opponentName;

    // Set colors based on player IDs
    if (localPlayerId === 1) {
      // Local is Player 1 (blue), opponent is Player 2 (red)
      p1NameEl.style.color = '#3b82f6';
      p2NameEl.style.color = '#ef4444';
    } else {
      // Local is Player 2 (red), opponent is Player 1 (blue)
      p1NameEl.style.color = '#ef4444';
      p2NameEl.style.color = '#3b82f6';
    }
  }

  /**
   * Show or hide player names display
   */
  private showPlayerNames(visible: boolean): void {
    if (!this.playerNamesDisplay) return;
    if (visible) {
      this.updatePlayerNames();
      this.playerNamesDisplay.classList.add('visible');
    } else {
      this.playerNamesDisplay.classList.remove('visible');
    }
  }

  /**
   * Show battle intro animation
   */
  private showBattleIntro(): void {
    console.log('[UIManager] showBattleIntro called, overlay:', !!this.battleIntroOverlay);
    if (!this.battleIntroOverlay) return;

    const localPlayerName = this.playerService.getName();
    const opponentName = this.gameController.getOpponentName();
    const localPlayerId = this.gameController.getLocalPlayerId();
    console.log('[UIManager] Showing intro:', localPlayerName, 'vs', opponentName, 'localPlayerId:', localPlayerId);

    // Determine player names and colors based on player ID
    // Player 1 is always blue (#3b82f6), Player 2 is always red (#ef4444)
    // Local player is shown on the left
    let p1Name: string;
    let p2Name: string;
    let p1Color: string;
    let p2Color: string;

    if (localPlayerId === 1) {
      p1Name = localPlayerName;
      p2Name = opponentName;
      p1Color = '#3b82f6'; // blue
      p2Color = '#ef4444'; // red
    } else {
      p1Name = localPlayerName;
      p2Name = opponentName;
      p1Color = '#ef4444'; // red (local is P2)
      p2Color = '#3b82f6'; // blue (opponent is P1)
    }

    this.battleIntroOverlay.show(p1Name, p2Name, p1Color, p2Color);
  }

  /**
   * Called when battle intro animation completes
   */
  private onBattleIntroComplete(): void {
    // Intro is just visual - no action needed when it completes
  }

  /**
   * Update unit count display
   * During preparation phase, shows "current/max" format to indicate remaining slots
   * During battle phase, shows "P1 vs P2" format
   */
  private updateUnitCount(): void {
    if (!this.unitCountIndicator) return;
    const info = this.gameController.getGameInfo();
    const p1 = this.gameController.getUnitCount(1);
    const p2 = this.gameController.getUnitCount(2);
    const maxUnits = this.gameController.getMaxUnitsForCurrentRound();

    // During preparation, show only player's slots (no opponent count)
    if (info.phase === GamePhase.Preparation) {
      const remaining = Math.max(0, maxUnits - p1);
      if (remaining > 0) {
        this.unitCountIndicator.innerHTML = `<span style="color: #4ade80;">${p1}/${maxUnits}</span>`;
      } else {
        this.unitCountIndicator.innerHTML = `<span style="color: #fbbf24;">${p1}/${maxUnits}</span>`;
      }
    } else {
      this.unitCountIndicator.textContent = `${p1} vs ${p2}`;
    }
  }

  /**
   * Update traits panel
   */
  private updateTraits(): void {
    if (!this.traitPanel) return;

    const traits = this.gameController.getActiveTraits(1);

    if (traits.length === 0) {
      this.traitPanel.innerHTML = '<div class="trait-inactive">No traits active</div>';
      return;
    }

    this.traitPanel.innerHTML = traits.map(trait => {
      const isActive = trait.activeThreshold !== null;
      const className = isActive ? 'trait-active' : 'trait-inactive';
      const threshold = trait.activeThreshold?.count || trait.nextThreshold?.count || '?';
      return `
        <div class="trait-item ${className}">
          <span>${trait.name}</span>
          <span>${trait.count}/${threshold}</span>
        </div>
      `;
    }).join('');
  }

  /**
   * Show round result overlay
   */
  private showRoundResult(winnerId: 0 | 1 | 2, p1Score?: number, p2Score?: number, isMatchEnd: boolean = false): void {
    if (!this.roundResultOverlay) return;

    const info = this.gameController.getGameInfo();
    const localPlayerId = this.gameController.isMultiplayerMode()
      ? this.gameController.getLocalPlayerId()
      : 1;

    // Use provided scores or fall back to game info
    const player1Score = p1Score ?? info.scores.player1;
    const player2Score = p2Score ?? info.scores.player2;

    // Determine if local player won
    const titleEl = document.getElementById('round-result-title');
    if (titleEl) {
      if (winnerId === 0) {
        titleEl.textContent = 'DRAW';
        titleEl.className = 'round-result-title draw';
      } else if (winnerId === localPlayerId) {
        titleEl.textContent = 'VICTORY';
        titleEl.className = 'round-result-title victory';
      } else {
        titleEl.textContent = 'DEFEAT';
        titleEl.className = 'round-result-title defeat';
      }
    }

    // Update scores
    const p1ScoreEl = document.getElementById('result-score-p1');
    const p2ScoreEl = document.getElementById('result-score-p2');
    if (p1ScoreEl) p1ScoreEl.textContent = String(player1Score);
    if (p2ScoreEl) p2ScoreEl.textContent = String(player2Score);

    // Handle countdown and back button visibility
    const countdownEl = document.getElementById('round-result-countdown');
    const backToMenuBtn = document.getElementById('btn-back-to-menu');

    if (isMatchEnd) {
      // Match end - show button, hide countdown
      if (countdownEl) countdownEl.style.display = 'none';
      if (backToMenuBtn) backToMenuBtn.classList.add('visible');
    } else {
      // Round end - show countdown, hide button
      if (countdownEl) countdownEl.style.display = 'block';
      if (backToMenuBtn) backToMenuBtn.classList.remove('visible');

      // Start countdown
      let countdown = 3;
      if (countdownEl) {
        countdownEl.textContent = `Next round in ${countdown}...`;
      }

      const countdownInterval = setInterval(() => {
        countdown--;
        if (countdownEl) {
          if (countdown > 0) {
            countdownEl.textContent = `Next round in ${countdown}...`;
          } else {
            countdownEl.textContent = 'Starting...';
          }
        }
        if (countdown <= 0) {
          clearInterval(countdownInterval);
        }
      }, 1000);
    }

    // Show overlay
    this.roundResultOverlay.classList.add('visible');
  }

  /**
   * Hide round result overlay
   */
  private hideRoundResult(): void {
    if (!this.roundResultOverlay) return;
    this.roundResultOverlay.classList.remove('visible');
  }

  /**
   * Show a toast notification
   */
  private showToast(message: string, type: 'warning' | 'error' = 'warning'): void {
    if (!this.toastContainer) return;

    const toast = document.createElement('div');
    toast.className = `toast ${type}`;
    toast.textContent = message;
    this.toastContainer.appendChild(toast);

    // Remove after animation completes
    setTimeout(() => {
      if (toast.parentNode) {
        toast.parentNode.removeChild(toast);
      }
    }, 2000);
  }

  /**
   * Clean up UI
   */
  dispose(): void {
    window.removeEventListener('keydown', this.onKeyDown);
    if (this.hudElement) {
      this.container.removeChild(this.hudElement);
    }
    if (this.roundResultOverlay) {
      this.container.removeChild(this.roundResultOverlay);
    }
    if (this.toastContainer) {
      this.container.removeChild(this.toastContainer);
    }
    if (this.shopPanel) {
      this.shopPanel.dispose();
    }
    if (this.bottomNavBar) {
      this.bottomNavBar.dispose();
    }
    if (this.charactersPanel) {
      this.charactersPanel.dispose();
    }
    if (this.battleLobbyPanel) {
      this.battleLobbyPanel.dispose();
    }
    if (this.playerNameModal) {
      this.playerNameModal.dispose();
    }
    if (this.battleIntroOverlay) {
      this.battleIntroOverlay.dispose();
    }
  }
}
